import { useState } from 'react';
import {
  ChatHeader,
  ChatInfoPanel,
  ChatInputArea,
  ChatMessageList,
  ChatDetails,
  Message,
  Participant
} from '.';
import { IonContent } from '@ionic/react';

type ChatViewModularProps = {
  userId: string;
  activeChat: string | null;
  // Data props
  messages: Message[];
  chatDetails: ChatDetails | null;
  participants: Participant[];
  newMessage: string;
  // Event handlers
  setNewMessage: (message: string) => void;
  onSendMessage: () => void;
  onImageUpload: (file: File) => void;
  onDeleteMessage: (messageId: string) => void;
  onAddReaction: (messageId: string, emoji: string) => void;
  onDeleteChat: () => void;
  onBackButton?: () => void;
  className?: string;
};

/**
 * A clean, modular chat view component that uses only the extracted sub-components.
 * This component is purely presentational and receives all data and handlers as props.
 */
export default function ChatViewModular({
  userId,
  activeChat,
  messages,
  chatDetails,
  participants,
  newMessage,
  setNewMessage,
  onSendMessage,
  onImageUpload,
  onDeleteMessage,
  onAddReaction,
  onDeleteChat,
  onBackButton,
  className
}: ChatViewModularProps) {
  const [showInfoPanel, setShowInfoPanel] = useState(false);

  if (!activeChat) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Select a chat to start messaging</p>
      </div>
    );
  }

  return (
    <>
      <ChatHeader
        chatDetails={chatDetails}
        participants={participants}
        userId={userId}
        toggleInfoPanel={() => setShowInfoPanel(!showInfoPanel)}
        onBackButton={onBackButton}
      />

      <IonContent fullscreen={true}>
        <ChatMessageList
          messages={messages}
          userId={userId}
          participants={participants}
          onDeleteMessage={onDeleteMessage}
          onAddReaction={onAddReaction}
        />

        <ChatInfoPanel
          chatDetails={chatDetails}
          participants={participants}
          userId={userId}
          isOpen={showInfoPanel}
          onClose={() => setShowInfoPanel(false)}
          onDeleteChat={onDeleteChat}
        />
      </IonContent>
      <ChatInputArea
        newMessage={newMessage}
        setNewMessage={setNewMessage}
        onSendMessage={onSendMessage}
        onImageUpload={onImageUpload}
      />
    </>
  );
}
